import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';

/**
 * 사용자 관리 페이지 (관리자 전용)
 * - 사용자 목록 조회, 생성, 수정, 삭제
 * - 기존 UserManagement 컴포넌트의 로직을 페이지 형태로 재구성
 */
const UserManagementPage = ({ showToast }) => {
  const [users, setUsers] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    name: '',
    password: '',
    role: 'user'
  });
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users/');
      setUsers(response.data.data);
    } catch (error) {
      console.error('사용자 목록 조회 실패:', error);
      if (showToast) {
        showToast('사용자 목록을 불러오는데 실패했습니다.');
      }
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // 입력 검증
    if (newUser.username.length < 4) {
      if (showToast) showToast('아이디는 4자 이상이어야 합니다.');
      return;
    }
    if (newUser.password.length < 6) {
      if (showToast) showToast('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    try {
      await axios.post('/users/', newUser);
      if (showToast) {
        showToast('사용자가 생성되었습니다.');
      }
      setIsCreateModalOpen(false);
      setNewUser({ username: '', name: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('사용자 생성 실패:', error);
      if (showToast) {
        showToast(error.response?.data?.message || '사용자 생성에 실패했습니다.');
      }
    }
  };

  const handleUpdateUser = async (userId) => {
    try {
      await axios.patch(`/users/${userId}`, {
        name: editingUser.name,
        role: editingUser.role
      });
      if (showToast) {
        showToast('사용자 정보가 수정되었습니다.');
      }
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('사용자 수정 실패:', error);
      if (showToast) {
        showToast('사용자 수정에 실패했습니다.');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('정말로 이 사용자를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await axios.delete(`/users/${userId}`);
      if (showToast) {
        showToast('사용자가 삭제되었습니다.');
      }
      fetchUsers();
    } catch (error) {
      console.error('사용자 삭제 실패:', error);
      if (showToast) {
        showToast('사용자 삭제에 실패했습니다.');
      }
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* 헤더 */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">사용자 관리</h1>
            <p className="text-secondary-600">시스템 사용자를 관리하고 권한을 설정하세요</p>
          </div>
          <button
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl shadow-medium hover:shadow-hard transition-all duration-200 flex items-center space-x-2"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>사용자 추가</span>
          </button>
        </div>

        {/* 사용자 목록 */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <table className="w-full">
              <thead className="bg-secondary-100 border-b border-secondary-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">아이디</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">이름</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">권한</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">생성일</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-secondary-600 uppercase tracking-wider">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-secondary-50 transition-colors duration-150">
                    <td className="px-6 py-4 text-sm text-secondary-900">{user.id}</td>
                    <td className="px-6 py-4 text-sm font-medium text-secondary-900">{user.username}</td>
                    <td className="px-6 py-4 text-sm text-secondary-900">
                      {editingUser && editingUser.id === user.id ? (
                        <input
                          type="text"
                          value={editingUser.name}
                          onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                          className="border border-secondary-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      ) : (
                        user.name
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {editingUser && editingUser.id === user.id ? (
                        <select
                          value={editingUser.role}
                          onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                          className="border border-secondary-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="user">일반</option>
                          <option value="admin">관리자</option>
                        </select>
                      ) : (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'admin'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role === 'admin' ? '관리자' : '일반'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-secondary-600">
                      {new Date(user.created_at).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {editingUser && editingUser.id === user.id ? (
                        <div className="flex justify-center space-x-2">
                          <button
                            className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors duration-150"
                            onClick={() => handleUpdateUser(user.id)}
                          >
                            저장
                          </button>
                          <button
                            className="px-3 py-1.5 bg-secondary-300 text-secondary-700 text-xs font-medium rounded-lg hover:bg-secondary-400 transition-colors duration-150"
                            onClick={() => setEditingUser(null)}
                          >
                            취소
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-center space-x-2">
                          <button
                            className="px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition-colors duration-150"
                            onClick={() => setEditingUser({ ...user })}
                          >
                            수정
                          </button>
                          <button
                            className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors duration-150"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            삭제
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          {users.length === 0 && (
            <div className="text-center py-12">
              <p className="text-secondary-500">등록된 사용자가 없습니다.</p>
            </div>
          )}
        </div>

        {/* 사용자 생성 모달 */}
        {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-hard animate-slide-up">
            <h3 className="text-2xl font-bold text-secondary-900 mb-6">사용자 추가</h3>
            <form onSubmit={handleCreateUser}>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  아이디 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="4자 이상"
                  required
                  minLength={4}
                />
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="이름 입력"
                  required
                />
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  비밀번호 <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="6자 이상"
                  required
                  minLength={6}
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  권한 <span className="text-red-500">*</span>
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="user">일반</option>
                  <option value="admin">관리자</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl shadow-medium hover:shadow-hard transition-all duration-200"
                >
                  생성
                </button>
                <button
                  type="button"
                  className="flex-1 px-6 py-3 bg-secondary-200 text-secondary-700 font-semibold rounded-xl hover:bg-secondary-300 transition-colors duration-200"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setNewUser({ username: '', name: '', password: '', role: 'user' });
                  }}
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;
